
import { HeadlinePathSegment } from '@/models/headline-path-segment';
import { NavEntry } from '@/models/nav-entry';
import { PathSegment } from '@/models/path-segment';
import { StoreState } from '@/store';
import { Actions } from '@/store/actions';
import { PathUtil } from '@/util/path-util';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import { computed, ComputedRef, defineComponent, onBeforeMount, ref, Ref } from 'vue';
import { useStore } from 'vuex';

export default defineComponent({
  // eslint-disable-next-line
  name: 'Navbar',
  setup() {
    const store = useStore();
    const mobileOpen: Ref<boolean> = ref(false);

    /**
     * Used to lock the pageChanged event to prevent
     * it from being called multiple times a second.
     */
    let pageChangeEventLocked = false;

    /**
     * Determines whether a given segment is a HeadlinePathSegment.
     * @param segment The segment to determine if it is a HeadlinePathSegment
     */
    function isHeadlineSegment(segment: PathSegment): boolean {
      return segment.type === 'HeadlinePathSegment';
    }

    /**
     * Sorting function for the headlines that sorts by the sortkey, or if not available, by the id.
     */
    function headlineSorter(
        a: PathSegment,
        b: PathSegment): number {
      const sortKeyA = a.dataObject?.sorting && b.dataObject?.sorting
          ? a.dataObject.sorting
          : a.dataObject?.id;
      const sortKeyB = b.dataObject?.sorting && a.dataObject?.sorting
          ? b.dataObject.sorting
          : b.dataObject?.id;

      if (!sortKeyA || !sortKeyB) {
        return 0;
      }

      if (sortKeyA > sortKeyB) {
        return 1;
      }
      if (sortKeyA < sortKeyB) {
        return -1;
      }
      return 0;
    }

    /**
     * Called when the view/"page" has changed.
     * Will scroll to the breadcrumb navigation.
     */
    function onPageChanged(): void {
      if (pageChangeEventLocked) {
        return;
      }

      pageChangeEventLocked = true;
      // root.$scrollTo('#funeral-configurator', {offset: -store.state.morticianSettings.scrollOffsetTop});
      mobileOpen.value = true;

      setTimeout(() => {
        pageChangeEventLocked = false;
      }, 600);

      setTimeout(() => {
        mobileOpen.value = false;
      }, 1500);
    }

    onBeforeMount(() => {      // => Setup page/view change events
      // set a watcher for when the currentPathSegment changes.
      // this covers all view changes except the result page.
      store.watch((state: StoreState) => state.currentPathSegment, onPageChanged);

      // set a watcher that fires when the summary is set active or inactive.
      // this covers the result page
      store.watch((state: StoreState) => state.summaryActive, onPageChanged);
    });

    /**
     * Returns if the contao instance thinks that this is a mobile device.
     */
    const isMobile: ComputedRef<boolean> = computed(() => {
      return store.state.isMobile;
    });

    const flashActive: ComputedRef<boolean> = computed(() => {
      return store.state.flashActive;
    });

    /**
     * Returns whether the summary is currently displayed.
     */
    const isSummaryActive: ComputedRef<boolean> = computed(() => {
      return store.state.summaryActive;
    });

    /**
     * Returns the current segment log from the store.
     */
    const segmentLog: ComputedRef<PathSegment[]> = computed(() => {
      return store.state.segmentLog;
    });

    /**
     * Returns the current calculated price ready for display (including currency symbol)
     */
    const currentCalculatedPriceForDisplay: ComputedRef<string> = computed(() => {
      return `${store.state.calculatedPrice.toFixed(0)} €`;
    });

    /**
     * Creates an array of nav entries from the segment log.
     * - The first headline of any given segment is used as the title for the nav entry
     * - If there is no headline, the title of the last segment is used.
     * - If there is no last segment, the title is 'Ihre Wahl'
     */
    const navEntries: ComputedRef<NavEntry[]> = computed(() => {
      let firstHeadline = store.state.pathSegments
                               .filter(isHeadlineSegment)
                               .map((segment: PathSegment) => segment as HeadlinePathSegment)
                               .sort(headlineSorter)
                               .map((hSegment: HeadlinePathSegment) => hSegment.content)[0];

      if (!firstHeadline) {
        firstHeadline = 'Ihre Wahl';
      }

      // Headlines can have HTML formatting, we need to remove that for the breadcrumb
      firstHeadline = sanitizeHtml(firstHeadline, {
        allowedTags: [],
        allowedAttributes: {}
      });

      let entries: NavEntry[] = [
        {title: firstHeadline, alias: 'start'}
      ];
      entries = entries.concat(segmentLog?.value.map((
          value,
          index,
          array) => {
        const headline = array[index].children
                                     .filter(isHeadlineSegment)
                                     .map((segment: PathSegment) => segment as HeadlinePathSegment)
                                     .sort(headlineSorter)
                                     .map((hSegment: HeadlinePathSegment) => hSegment.content)[0];

        let title = headline ?? value.title;
        // Headlines can have HTML formatting, we need to remove that for the breadcrumb
        title = sanitizeHtml(title, {
          allowedTags: [],
          allowedAttributes: {}
        });

        return {title, alias: value.alias, segment: value};
      }));

      if (store.state.summaryAvailable) {
        entries.push({title: `Preisschätzung: ${currentCalculatedPriceForDisplay.value}`, alias: 'summary'});
      }

      return entries;
    });

    /**
     * returns the current @{PathSegment}
     */
    const currentPathSegment: ComputedRef<PathSegment> = computed(() => {
      return isSummaryActive.value
          ? null
          : store.state.currentPathSegment;
    });

    /**
     * Returns whether the given navEntry meets the requirements to be considered 'active'.
     * @param navEntry The nav entry to return the active status for.
     */
    function isActiveState(navEntry: NavEntry): boolean {
      if (navEntry.alias === 'summary' && isSummaryActive) {
        return true;
      }

      if ((navEntries.value[0] && navEntry === navEntries.value[0]) && !currentPathSegment.value && !isSummaryActive.value) {
        return true;
      }

      return !isSummaryActive.value && currentPathSegment.value === navEntry.segment;
    }

    /**
     * Called when the user clicks on a segment link.
     * @param ev the JavaScript DOM event
     * @param navEntry the nav entry that has been clicked.
     */
    function onSegmentLinkClick(
        ev: Event,
        navEntry: NavEntry): void {
      ev.preventDefault();
      if (navEntry.segment) {
        const selectedSegment = currentPathSegment.value
            ? currentPathSegment?.value?.children?.find(segment => segment.chosen)
            : store.state.pathSegments.find((segment: PathSegment) => segment.chosen);

        (!!selectedSegment && selectedSegment != navEntry.segment && !PathUtil.isSegmentOnPath(
            segmentLog.value, selectedSegment))
            ? store.dispatch(Actions.SubmitPathSegmentSelection, selectedSegment)
            : store.dispatch(Actions.SubmitPathSegmentSelection, navEntry.segment);
      } else {
        if (navEntry.alias === 'summary') {
          // this.$store.dispatch(Actions.ShowPathSummary);
          return;
        }

        store.dispatch(Actions.SubmitPathSegmentSelection, null);
      }
    }

    function decodeHtmlEntities(input: string): string {
      return he.decode(input);
    }

    return {
      isMobile,
      flashActive,
      isSummaryActive,
      currentCalculatedPriceForDisplay,
      navEntries,
      currentPathSegment,
      isActiveState,
      onSegmentLinkClick,
      decodeHtmlEntities,
      pageChangeEventLocked,
      mobileOpen
    };
  }
});
